import { Link } from "react-router-dom";
import type { CrewMember } from "@/mocks/admin/crew";

type Props = {
  member: CrewMember;
};

export default function OverviewPanel({ member }: Props) {
  const firstName = member.name.split(" ")[0];
  const ytdPay = Math.round(932 * member.hourlyRate); // synthesized YTD
  const cards = [
    {
      label: "Completed moves",
      value: member.completedMoves.toString(),
      sub: "lifetime",
      icon: "ri-check-double-line",
      tint: "bg-emerald-50 text-emerald-700 border-emerald-100",
    },
    {
      label: "Avg rating",
      value: member.rating.toFixed(1),
      sub: "from customer surveys",
      icon: "ri-star-line",
      tint: "bg-amber-50 text-amber-700 border-amber-100",
    },
    {
      label: "Hours this week",
      value: `${member.weeklyHours}h`,
      sub: `target ${member.weeklyTarget}h`,
      icon: "ri-time-line",
      tint: "bg-brand-50 text-brand-700 border-brand-100",
    },
    {
      label: "YTD earnings",
      value: `$${(ytdPay / 1000).toFixed(1)}k`,
      sub: "before taxes",
      icon: "ri-money-dollar-circle-line",
      tint: "bg-indigo-50 text-indigo-700 border-indigo-100",
    },
  ];

  const timeline = [
    {
      icon: "ri-login-circle-line",
      tint: "bg-emerald-500",
      title: `Clocked in at ${member.currentProject || "MV-2041"}`,
      meta: `Today · ${member.clockedInAt || "07:24"}`,
    },
    {
      icon: "ri-checkbox-circle-line",
      tint: "bg-brand-500",
      title: "Completed shift on MV-2039",
      meta: "Yesterday · 8h 12m logged",
    },
    {
      icon: "ri-award-line",
      tint: "bg-amber-500",
      title: "5-star customer review from Mendez family",
      meta: "2 days ago · MV-2038",
    },
    {
      icon: "ri-truck-line",
      tint: "bg-indigo-500",
      title: "Dispatched as lead on Genesis warehouse move",
      meta: "May 28 · 9h 24m",
    },
    {
      icon: "ri-graduation-cap-line",
      tint: "bg-ink-700",
      title: "Completed annual safety re-certification",
      meta: "May 22",
    },
  ];

  return (
    <div className="space-y-5">
      {/* Headline summary card */}
      <div className="bg-white border border-ink-100 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-quill-pen-line text-ink-500"></i>
          <h3 className="font-display text-base font-bold text-ink-900">Manager summary</h3>
        </div>
        <p className="text-sm text-ink-700 leading-relaxed">
          {firstName} is a {member.rating >= 4.8 ? "top-performing" : "reliable"} {member.role.toLowerCase()} on the {member.city.split(",")[0]} crew with{" "}
          <span className="font-bold">{member.completedMoves}</span> completed moves and an average rating of{" "}
          <span className="font-bold text-amber-700">{member.rating}★</span>.
          {member.weeklyHours >= member.weeklyTarget * 0.9
            ? ` Currently hitting full hours (${member.weeklyHours}/${member.weeklyTarget}h this week).`
            : ` Has capacity for more hours this week (${member.weeklyHours}/${member.weeklyTarget}h booked).`}{" "}
          Specializes in {member.skills.slice(0, 3).join(", ").toLowerCase()}.
        </p>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div
            key={c.label}
            className="bg-white border border-ink-100 rounded-lg p-4 flex flex-col"
          >
            <div
              className={`w-9 h-9 rounded-md flex items-center justify-center mb-2.5 border ${c.tint}`}
            >
              <i className={`${c.icon} text-base`}></i>
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">
              {c.label}
            </div>
            <div className="font-display text-2xl font-bold text-ink-900 mt-0.5 tabular-nums leading-tight">
              {c.value}
            </div>
            <div className="text-[10px] text-ink-400 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div className="bg-white border border-ink-100 rounded-lg">
        <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-ink-500"></i>
            <h3 className="font-display text-base font-bold text-ink-900">Recent activity</h3>
          </div>
          <span className="text-[11px] text-ink-500">Last 7 days</span>
        </div>
        <div className="px-5 py-4">
          <ol className="relative">
            <span className="absolute left-[11px] top-2 bottom-2 w-px bg-ink-100"></span>
            {timeline.map((t, i) => (
              <li key={i} className="relative pl-8 pb-4 last:pb-0">
                <span
                  className={`absolute left-0 top-0.5 w-6 h-6 rounded-full flex items-center justify-center text-white ${t.tint}`}
                >
                  <i className={`${t.icon} text-xs`}></i>
                </span>
                <div className="text-sm font-semibold text-ink-900 leading-tight">
                  {t.title}
                </div>
                <div className="text-[11px] text-ink-500 mt-0.5">{t.meta}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Quick links */}
      <div className="bg-white border border-ink-100 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-flashlight-line text-ink-500"></i>
          <h3 className="font-display text-base font-bold text-ink-900">Quick links</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <Link
            to="/timeclock"
            className="flex items-center gap-2.5 p-3 rounded-md border border-ink-100 hover:bg-cream-50 cursor-pointer"
          >
            <span className="w-8 h-8 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold text-ink-900">Timeclock</div>
              <div className="text-[10px] text-ink-500 truncate">Review live shifts</div>
            </div>
          </Link>
          <Link
            to="/projects"
            className="flex items-center gap-2.5 p-3 rounded-md border border-ink-100 hover:bg-cream-50 cursor-pointer"
          >
            <span className="w-8 h-8 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center">
              <i className="ri-truck-line"></i>
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold text-ink-900">Projects</div>
              <div className="text-[10px] text-ink-500 truncate">Assign to a job</div>
            </div>
          </Link>
          <Link
            to="/feedback"
            className="flex items-center gap-2.5 p-3 rounded-md border border-ink-100 hover:bg-cream-50 cursor-pointer"
          >
            <span className="w-8 h-8 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center">
              <i className="ri-star-line"></i>
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold text-ink-900">Reviews</div>
              <div className="text-[10px] text-ink-500 truncate">See customer feedback</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}